import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { Button, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import type { ThemeHeadingSizes } from '@rover/kibble/styles/themeTypes';
import { HeadingVariant } from '@rover/react-lib/src/components/lists/Carousel/components/Header';
import { SeoLocationDetails } from '@rover/rsdk/src/apiClient/latest/model/seoLocationDetails';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import getBrandDisplayName from '@rover/shared/js/utils/getBrandDisplayName';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';

import getSearchUrl from '../../../../utils/getSearchUrl';
import ReviewsCarousel from '../../components/ReviewsCarousel';
import {
  ReviewsCarouselPaginateAction,
  ReviewsCtaClick,
  ViewReviewCard,
} from '../../utilities/analyticsEvents';
import { PageSectionId, useSectionRef } from '../hooks/useSectionRef';

const getHeading = (petType, brandDisplayName, locationName): string => {
  switch (petType) {
    case PetType.CAT:
      return t`Cat parents share their ${brandDisplayName} experiences - ${locationName}`;
    case PetType.DOG:
      return t`Dog parents share their ${brandDisplayName} experiences - ${locationName}`;
    case PetType.PUPPY:
      return t`Puppy parents share their ${brandDisplayName} experiences - ${locationName}`;
    default:
      return t`Pet parents share their ${brandDisplayName} experiences - ${locationName}`;
  }
};
type Props = {
  seoLocation: {
    details: Pick<SeoLocationDetails, 'name' | 'serviceTypeSlug' | 'petType'>;
  };
  locale: Locale;
  searchFilters: Partial<SearchFilters>;
  headingVariant?: HeadingVariant;
  headingSize?: ThemeHeadingSizes;
} & Pick<React.ComponentProps<typeof ReviewsCarousel>, 'reviews'> &
  Pick<React.ComponentProps<typeof Section>, 'my' | 'mt' | 'mb'>;

const SeoLocationReviewsCarousel: React.FC<Props> = ({
  seoLocation: { details },
  reviews,
  locale,
  searchFilters,
  headingVariant = 'heading',
  headingSize = '400',
  ...marginProps
}) => {
  const ref = useSectionRef(PageSectionId.ReviewsCarousel);
  const { petType, name: locationName } = details;
  const brandDisplayName = getBrandDisplayName();

  if (!reviews.length) return null;

  return (
    <Section htmlRef={ref} paddingLeft="4x" {...marginProps}>
      <Flex gap="6x" flexDirection="column" alignItems="center">
        <ReviewsCarousel
          heading={getHeading(petType, brandDisplayName, locationName)}
          headingVariant={headingVariant}
          headingSize={headingSize}
          reviews={reviews}
          onCardViewed={(index) => emitAnalyticsEvent(new ViewReviewCard({ index }))}
          cardProps={{
            href: getSearchUrl(searchFilters, locale),
            hrefLabel: t`Find pet care near you`,
          }}
          onPaginate={(paginateParams) =>
            emitAnalyticsEvent(
              new ReviewsCarouselPaginateAction({
                serviceTypeSlug: details.serviceTypeSlug,
                ...paginateParams,
              })
            )
          }
        />
        <Button
          width="100%"
          maxWidth={['335px', '375px']}
          href={getSearchUrl(searchFilters, locale)}
          onPress={() => {
            new ReviewsCtaClick({
              serviceTypeSlug: details.serviceTypeSlug,
            }).fireCrossPageEvent();
          }}
        >
          <Trans>Find pet care near you</Trans>
        </Button>
      </Flex>
    </Section>
  );
};

export default SeoLocationReviewsCarousel;
